"use client";

import React, {
  ComponentProps,
  Fragment,
  isValidElement,
  MouseEvent,
  useCallback,
  useMemo,
} from "react";
import { TableColumn } from "./TableHeader";
import { useVirtualizer } from "@tanstack/react-virtual";
import TableRow from "./TableRow";
import TableHeader from "./TableHeader";
import PagingIndicator from "../PagingIndicator";
import { LingoError } from "../../constants/lingoError";
import Flex, { FlexProps } from "../Flex";
import Box, { BoxProps } from "../Box";
import Text from "../Text";
import Tooltip from "../Tooltip";
import Button, { ButtonProps } from "../Button";
import ActivityIndicator from "../ActivityIndicator";
import EmptyState from "../EmptyState";
import { AnyObject } from "../../models";

type TableDataProps<DataType> = {
  data: DataType[];
  isLoading?: boolean;
  error?: LingoError;
  fetchNextPage?: () => void;
};

type TableCheckboxProps<DataType> = {
  disabled?: (data: DataType) => boolean;
  isSelected: (data: DataType) => boolean;
  onClick: (data: DataType) => void;
  CheckboxCell?: React.ComponentType<{ data: DataType; rowIndex: number }>;
};

type HeaderProps = {
  title: string;
  subtitle?: string | React.ReactNode;
  subtitleButton?: ButtonProps;
  tooltip?: string | React.ReactNode;
  actions?: React.ReactNode;
} & FlexProps &
  BoxProps;

type Props<DataType> = {
  header?: HeaderProps;
  rowSize?: "small" | "medium" | "large";
  hasMorePages?: boolean;
  tableData: TableDataProps<DataType>;
  onRowClick?: (data: DataType, event: MouseEvent) => void;
  columns: TableColumn[];
  checkboxProps?: TableCheckboxProps<DataType>;
  hideFinalBottomBorder?: boolean;
  dynamicHeight?: boolean;
  renderContextMenuItems?: (data: DataType) => React.ReactNode;
  sort?: string;
  onSort?: (sortKey: string) => void;
  emptyState?: ComponentProps<typeof EmptyState> | React.ReactNode;
  errorState?: ComponentProps<typeof EmptyState> | React.ReactNode;
} & FlexProps &
  BoxProps;

const Table = <DataType,>({
  header = null,
  rowSize = "medium",
  hasMorePages,
  tableData,
  onRowClick,
  columns,
  checkboxProps,
  hideFinalBottomBorder = false,
  dynamicHeight,
  renderContextMenuItems,
  sort,
  onSort,
  emptyState,
  errorState,
  ...styles
}: Props<DataType>) => {
  const parentScrollRef = React.useRef<HTMLDivElement>(null);

  const { data, isLoading, error, fetchNextPage } = tableData;

  const isEmpty = useMemo(
    () => !isLoading && !error && data?.length === 0,
    [data?.length, isLoading, error]
  );

  let count = data?.length;
  if (count && hasMorePages) count += 1;

  const getHeight = useCallback(() => {
    const tableRowHeights = { small: 40, medium: 80, large: 136 };

    return tableRowHeights[rowSize];
  }, [rowSize]);

  const rowVirtualizer = useVirtualizer({
    count,
    getScrollElement: () => parentScrollRef.current,
    estimateSize: getHeight,
    overscan: 3,
  });

  const headerSpacer = checkboxProps ? 56 : 0;

  function renderHeader() {
    if (!header?.title) return;
    const { title, subtitle, subtitleButton, actions, tooltip, ...styling } = header;
    return (
      <Flex
        alignItems="center"
        borderBottom="default"
        borderRadius="4px 4px 0 0"
        background="grayLightest"
        px="24px"
        py="16px"
        {...styling}>
        <Box flex="1">
          {title && (
            <Text mb="xxs" font="ui.regularBold">
              {title}
            </Text>
          )}

          {subtitle && (
            <Flex alignItems="center">
              <Text as="div" color="grayDarkest">
                {subtitle}
              </Text>
              {subtitleButton && <Button buttonStyle="tertiary" ml="xs" {...subtitleButton} />}
              {tooltip && (
                <Tooltip.InfoTip direction="bottom" fill="grayDarkest">
                  {tooltip}
                </Tooltip.InfoTip>
              )}
            </Flex>
          )}
        </Box>
        {actions && <Box>{actions}</Box>}
      </Flex>
    );
  }

  function renderContent() {
    if (isEmpty) {
      if (isValidElement(emptyState)) return emptyState;
      return (
        <Box>
          <EmptyState
            styleOverrides={{ py: "xxl" }}
            title="No data"
            subtitle="There is nothing to show here."
            {...(emptyState as AnyObject)}
          />
        </Box>
      );
    } else if (isLoading && !data?.length) {
      return <ActivityIndicator mt="xxl" center />;
    } else if (error && !data?.length) {
      return isValidElement(errorState) ? (
        errorState
      ) : (
        <EmptyState
          title="Unable to load data"
          subtitle={error?.message}
          iconProps={{ iconId: "error", fill: "error" }}
          button={{ text: "Retry", onClick: fetchNextPage }}
          {...(errorState as AnyObject)}
        />
      );
    } else {
      return renderTable();
    }
  }

  function renderTable() {
    return (
      <div ref={parentScrollRef} style={{ overflowX: "auto" }}>
        <TableHeader
          columns={columns.map((col, i) => ({
            ...col,
            leftPadding: i === 0 && !headerSpacer ? "40px" : "0",
          }))}
          sort={sort}
          onSort={onSort}
          headerSpacer={headerSpacer}
        />
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}>
          {rowVirtualizer.getVirtualItems().map(virtualRow => {
            const isLoaderRow = virtualRow.index > data.length - 1;
            return (
              <Fragment key={virtualRow.key}>
                {isLoaderRow ? (
                  <PagingIndicator
                    mt={`${virtualRow.size / 2}px`}
                    transform={`translateY(-50%)`}
                    hasMore={hasMorePages}
                    data={data}
                    error={error}
                    fetchNextPage={fetchNextPage}
                    isLoading={isLoading}
                  />
                ) : (
                  <TableRow
                    row={virtualRow}
                    columns={columns}
                    dynamicHeight={dynamicHeight}
                    data={data}
                    hideFinalBottomBorder={hideFinalBottomBorder}
                    getHeight={getHeight}
                    onRowClick={onRowClick}
                    checkboxProps={checkboxProps}
                    renderContextMenuItems={renderContextMenuItems}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <Flex position="relative" flexDirection="Column" width="100%" {...styles}>
      {renderHeader()}
      {renderContent()}
    </Flex>
  );
};

export default Table;
